"use client";

import Image from "next/image";
import { useSyncExternalStore } from "react";
import styles from "./home.module.css";

import { getStorefrontUrl } from "@/lib/storefront-url";
import type { CompanyBusinessType } from "@/types/company";

type SupplierCategory = {
  name: string;
  slug: string;
};

export type Supplier = {
  id: number;
  business_type: CompanyBusinessType;
  trade_name: string;
  slug: string;
  custom_subdomain_active: boolean;
  custom_domain_active: string | null;
  city: string;
  state: string;
  logo_url: string | null;
  categories: SupplierCategory[];
};

function normalizePublicUrl(
  value: string | null,
): string | null {
  if (!value) {
    return null;
  }

  if (value.startsWith("http://api-dev.fornecedor.net")) {
    return value.replace(
      "http://api-dev.fornecedor.net",
      "https://api-dev.fornecedor.net",
    );
  }

  if (value.startsWith("http://api.fornecedor.net")) {
    return value.replace(
      "http://api.fornecedor.net",
      "https://api.fornecedor.net",
    );
  }

  return value;
}

function getInitials(name: string) {
  const words = name
    .trim()
    .split(/\\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return "FN";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return (
    words[0][0] +
    words[words.length - 1][0]
  ).toUpperCase();
}

function getLocation(supplier: Supplier) {
  return (
    [supplier.city, supplier.state]
      .filter(Boolean)
      .join(", ") ||
    "Localização não informada"
  );
}

const featuredBusinessInfo: Record<
  string,
  {
    category: string;
    badge: string;
    position: number;
  }
> = {
  artesanatodobebe: {
    category: "Roupas e produtos infantis",
    badge: "Comércio",
    position: 1,
  },
  amjotanotebooks: {
    category: "Notebooks, manutenção e tecnologia",
    badge: "Serviços",
    position: 2,
  },
  marcoaurelioimoveis: {
    category: "Compra, venda e locação de imóveis",
    badge: "Imobiliária",
    position: 3,
  },
  mariahelena: {
    category: "Vestidos e moda infantil",
    badge: "Comércio",
    position: 4,
  },
};

function getBusinessInfo(
  supplier: Supplier,
): {
  category: string;
  badge: string;
} {
  const featured =
    featuredBusinessInfo[supplier.slug];

  if (featured) {
    return featured;
  }

  switch (supplier.business_type) {
    case "real_estate":
      return {
        category: "Compra, venda e locação de imóveis",
        badge: "Imobiliária",
      };

    case "vehicles":
      return {
        category: "Compra e venda de veículos",
        badge: "Veículos",
      };

    case "food":
      return {
        category: "Alimentação e delivery",
        badge: "Alimentação",
      };

    case "services":
      return {
        category: "Serviços profissionais",
        badge: "Serviços",
      };

    case "consortium_insurance":
      return {
        category: "Consórcios, proteção e seguros",
        badge: "Consórcios e Seguros",
      };

    case "wholesale":
      return {
        category: "Produtos para empresas e revendedores",
        badge: "Atacado",
      };

    case "retail":
      return {
        category: "Produtos para você e sua família",
        badge: "Comércio",
      };

    default:
      return {
        category: "Produtos e serviços",
        badge: "Negócio",
      };
  }
}

type SupplierPreviewProps = {
  suppliers: Supplier[];
  compact?: boolean;
  initialUrls?: Record<number, string>;
};

const subscribeToHydration = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

export default function SupplierPreview({
  suppliers,
  compact = false,
  initialUrls,
}: SupplierPreviewProps) {
  const hydrated = useSyncExternalStore(subscribeToHydration, clientSnapshot, serverSnapshot);
  if (suppliers.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className={styles.suppliers}>
        {suppliers.map((supplier) => {
          const logo = normalizePublicUrl(supplier.logo_url);
          return (
            <a
              key={supplier.id}
              className={styles.supplier}
              href={!hydrated && initialUrls?.[supplier.id] ? initialUrls[supplier.id] : getStorefrontUrl(supplier)}
            >
              <span className={styles.supplierLogo}>
                {logo ? <Image src={logo} alt="" fill sizes="80px" unoptimized /> : getInitials(supplier.trade_name)}
              </span>
              <strong>{supplier.trade_name}</strong>
              <small>{getBusinessInfo(supplier).category}</small>
              <small>{getLocation(supplier)}</small>
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full min-w-0 max-w-[570px] lg:mx-0">
      <div className="absolute -left-10 top-14 h-40 w-40 rounded-full bg-blue-200/60 blur-3xl" />
      <div className="absolute -right-8 bottom-10 h-44 w-44 rounded-full bg-blue-100/70 blur-3xl" />

      <div className="relative overflow-hidden rounded-[30px] border border-[var(--border)] bg-white p-4 shadow-[0_30px_80px_rgba(15,23,42,0.12)] sm:p-6">
        <div className="mb-5 flex min-w-0 flex-col items-start gap-3 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--brand)]">
              Negócios no Fornecedor.Net
            </p>

            <p className="mt-1 text-lg font-extrabold text-slate-950">
              Uma plataforma para diferentes segmentos
            </p>
          </div>

          <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-[var(--brand)]">
            Negócios reais
          </span>
        </div>

        <div className="space-y-3">
          {suppliers.map((supplier, index) => {
            const businessInfo =
              getBusinessInfo(supplier);

            const logoUrl =
              normalizePublicUrl(
                supplier.logo_url,
              );

            return (
              <a
                key={supplier.id}
                href={getStorefrontUrl({
                  slug: supplier.slug,
                  custom_subdomain_active:
                    supplier.custom_subdomain_active,
                  custom_domain_active:
                    supplier.custom_domain_active,
                })}
                className={
                  "block rounded-2xl border p-4 transition-transform hover:-translate-y-0.5 " +
                  (index === 0
                    ? "border-blue-200 bg-blue-50/70"
                    : "border-slate-200 bg-white")
                }
              >
                <div className="flex items-start gap-3.5">
                  <div className={`relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl text-sm font-extrabold text-[var(--brand)] ${logoUrl ? "" : "border border-slate-200 bg-white"}`}>
                    {logoUrl ? (
                      <Image
                        src={logoUrl}
                        alt={
                          "Logo da empresa " +
                          supplier.trade_name
                        }
                        fill
                        sizes="48px"
                        unoptimized
                        className="object-cover"
                      />
                    ) : (
                      getInitials(
                        supplier.trade_name,
                      )
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-start">
                      <div>
                        <h3 className="truncate font-extrabold text-slate-950">
                          {supplier.trade_name}
                        </h3>

                        <p className="mt-0.5 text-sm text-slate-500">
                          {businessInfo.category}
                        </p>
                      </div>

                      <span className="w-fit max-w-[150px] truncate rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[var(--brand)] ring-1 ring-blue-200">
                        {businessInfo.badge}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />

                        <circle
                          cx="12"
                          cy="10"
                          r="2.5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>

                      {getLocation(supplier)}
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-slate-950 p-4 text-white">
          <div>
            <strong className="block text-base font-extrabold sm:text-lg">
              Brasil
            </strong>

            <span className="text-[11px] text-slate-400">
              alcance nacional
            </span>
          </div>

          <div className="border-x border-white/10 px-3">
            <strong className="block text-base font-extrabold sm:text-lg">
              Busca
            </strong>

            <span className="text-[11px] text-slate-400">
              por categoria
            </span>
          </div>

          <div className="pl-2">
            <strong className="block text-base font-extrabold sm:text-lg">
              Contato
            </strong>

            <span className="text-[11px] text-slate-400">
              direto
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
